import { AddressLinkDropdownPreview } from '@Components/AddressLinkDropdownPreview';
import { Badges } from '@Components/Badges';
import { LinkedFieldWithLayoutSettings } from '@Components/LinkedFieldWithLayoutSettings';
import { addressColumns } from '@Constants/TableColumns';
import { useBadges } from '@Hooks/useBadges';
import type { ApiAddress, ApiStorageLocation, EnumBusinessState } from '@Model';
import { enumBusinessStateOptions, EnumFrontendLayoutSetting } from '@Model';
import { FrameEditAddress } from '@Modules/Stammdaten';
import {
    type AsyncStateUpdate,
    FlexRow,
    FloatingCard,
    Grid,
    GridColumn,
    GridColumns,
    type IGuiBinder,
    Select,
    TextInput,
    useAutoConfirmed,
    useLinkedField,
    useLinkProperty,
    useProp,
    useTranslate,
} from '@synqo/frontend-component-library';
import { memo } from 'react';

// @ai-generated 2026-10-21T10:16:14+00:00 by matthias.gessinger@synqony.com using claude-sonnet-5-5

interface IProps {
    binder: IGuiBinder<ApiStorageLocation>;
    baseApiObjectPath: string;
    requestUpdateState: AsyncStateUpdate<ApiStorageLocation>;

    shorthand: string;
    label: string;
    businessState: EnumBusinessState;
}

export const StorageLocationMainSection = memo(function StorageLocationMainSection({
    binder,
    baseApiObjectPath,
    requestUpdateState,
    shorthand,
    label,
    businessState,
}: IProps) {
    const i18n = useTranslate('');

    const linkAddress = useLinkedField<ApiStorageLocation, ApiAddress>(
        baseApiObjectPath,
        'address',
        FrameEditAddress,
        true,
        requestUpdateState,
    );

    const shorthandField = useProp(binder, 'shorthand');
    const labelField = useProp(binder, 'label');
    const businessStateField = useAutoConfirmed<ApiStorageLocation, EnumBusinessState>(binder, 'business_state');
    const numberField = useProp(binder, 'number');
    const addressField = useLinkProperty(binder, linkAddress.linkApi);
    const statusBadges = useBadges({ business_state: businessState });
    const remarksField = useProp(binder, 'remarks');

    const summary = (
        <FlexRow gap="xs">
            {`${shorthand} • ${label}`}
            <Badges badges={statusBadges} />
        </FlexRow>
    );

    return (
        <FloatingCard title="Allgemein" separateHeader collapsible additionalHeaderItems={summary}>
            <Grid columns={12}>
                <GridColumns spans={[3, 6, 3]}>
                    <TextInput {...shorthandField} label={i18n('Suchcode')} />
                    <TextInput {...labelField} label={i18n('Bezeichnung')} />
                    <Select items={enumBusinessStateOptions} {...businessStateField} label={i18n('Status')} />
                </GridColumns>
                <GridColumns spans={[3, 9]}>
                    <TextInput {...numberField} label={i18n('Nummer')} />
                    <LinkedFieldWithLayoutSettings<ApiAddress>
                        layoutSettingsContext={EnumFrontendLayoutSetting.AddressesTableLayout}
                        {...addressField}
                        {...linkAddress.events}
                        columns={addressColumns}
                        label={i18n('Anschrift')}
                        dropdownPreview={AddressLinkDropdownPreview}
                    />
                </GridColumns>
                <GridColumn span={12}>
                    <TextInput {...remarksField} label={i18n('Bemerkung')} inputSize={3} />
                </GridColumn>
            </Grid>
        </FloatingCard>
    );
});
